/**
 * Fase 5 — peso, IMC (tabela da OMS para adultos), calorias e a chave de compartilhamento com a equipe.
 * O atleta digita o peso (semanal ou mensal, à escolha dele); o IMC é calculado no servidor.
 */
import { useState, type FormEvent } from "react";
import { api, ApiError } from "../api/client";
import type { BmiInfo, BodyPayload, CaloriesSummary, MyBody, StaffBody } from "../api/types";
import { formatDate } from "../lib/format";
import { useAsync } from "../lib/useAsync";
import { Link } from "../router/router";
import { ChartFrame, StackedBars, TimeChart } from "./charts";
import { Badge, Button, EmptyState, ErrorState, FormError, Loading, SelectField, TextField, useToast } from "./ui";
import { Kpi } from "./performance";
import { LOCALE, t, tj, tn } from "../i18n";

const kg = (v: number) => `${v.toLocaleString(LOCALE, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg`;
const kcal = (v: number) => `${Math.round(v).toLocaleString(LOCALE)} kcal`;
const num1 = (v: number) => v.toLocaleString(LOCALE, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const signed = (v: number) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v).toLocaleString(LOCALE, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`;
const shortDate = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const toNum = (s: string) => parseFloat(s.replace(",", "."));

const BMI_TONE: Record<string, "ok" | "warn" | "danger"> = {
  thinness_3: "danger", thinness_2: "warn", thinness_1: "warn", normal: "ok",
  overweight: "warn", obese_1: "danger", obese_2: "danger", obese_3: "danger",
};

// Escala visual: 15 a 40 kg/m². Faixas da OMS: 18,5 · 25 · 30 · 35 · 40.
const SCALE_MIN = 15;
const SCALE_MAX = 40;
const SEGMENTS = [
  { to: 18.5, cls: "bmi-seg-thin" },
  { to: 25, cls: "bmi-seg-normal" },
  { to: 30, cls: "bmi-seg-over" },
  { to: 40, cls: "bmi-seg-obese" },
];
const pos = (v: number) => `${((Math.min(SCALE_MAX, Math.max(SCALE_MIN, v)) - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100}%`;

export function BmiCard({ bmi, weight, date }: { bmi: BmiInfo | null; weight: number; date: string }) {
  if (!bmi) {
    return (
      <div className="bmi-card">
        <div className="bmi-value"><strong>{kg(weight)}</strong><span className="muted">{t("em {date}", { date: formatDate(date) })}</span></div>
        <p className="muted">{t("Informe a altura para calcular o IMC.")}</p>
      </div>
    );
  }
  let prev = SCALE_MIN;
  return (
    <div className="bmi-card">
      <div className="bmi-value">
        <strong>{kg(weight)}</strong>
        <span>{tj("IMC {v} kg/m²", { v: <strong style={{ fontSize: "inherit" }}>{num1(bmi.value)}</strong> })}</span>
        {bmi.label && <Badge tone={BMI_TONE[bmi.key ?? "normal"]}>{bmi.label}</Badge>}
        <span className="muted">{t("em {date}", { date: formatDate(date) })}</span>
      </div>
      {bmi.adult_scale ? (
        <div role="img" aria-label={t("IMC {value}: {label}. Faixas da OMS: abaixo de 18,5; 18,5 a 24,9; 25 a 29,9; 30 ou mais.", { value: num1(bmi.value), label: bmi.label })}>
          <div className="bmi-scale">
            {SEGMENTS.map((s) => {
              const w = ((s.to - prev) / (SCALE_MAX - SCALE_MIN)) * 100;
              prev = s.to;
              return <span key={s.to} className={`seg ${s.cls}`} style={{ width: `${w}%` }} />;
            })}
            <span className="bmi-marker" style={{ left: pos(bmi.value) }} />
          </div>
          <div className="bmi-ticks" aria-hidden>
            {[18.5, 25, 30, 35, 40].map((tp) => (
              <span key={tp} style={{ left: pos(tp) }}>{String(tp).replace(".", ",")}</span>
            ))}
          </div>
        </div>
      ) : null}
      {bmi.note && <p className="muted" style={{ fontSize: "var(--step--1)" }}>{bmi.note}</p>}
    </div>
  );
}

function WeightCharts({ body, from, to }: { body: BodyPayload; from: string; to: string }) {
  const m = body.measurements;
  const c = body.period_change;
  return (
    <>
      {c && (
        <dl className="kpis" style={{ marginBottom: 12 }}>
          <Kpi label={t("Variação no período")} value={`${signed(c.change_kg)} kg`} sub={t("{change_pct} % · de {start_kg} para {end_kg} kg", { change_pct: signed(c.change_pct), start_kg: num1(c.start_kg), end_kg: num1(c.end_kg) })} />
          <Kpi label={t("Menor e maior peso")} value={`${num1(c.min_kg)} – ${num1(c.max_kg)} kg`} />
        </dl>
      )}
      <ChartFrame
        title={t("Peso")}
        subtitle={t("Cada ponto é um peso informado pelo atleta.")}
        table={{ head: [t("Data"), t("Peso"), "IMC"], rows: m.map((p) => [formatDate(p.measured_on), kg(p.weight_kg), p.bmi != null ? num1(p.bmi) : "—"]) }}
        empty={m.length === 0 ? t("Nenhum peso registrado no período.") : null}
      >
        <TimeChart
          points={m.map((p) => ({ key: p.measured_on, date: p.measured_on, value: p.weight_kg, title: formatDate(p.measured_on), extra: p.bmi != null ? [`IMC ${num1(p.bmi)}`] : [] }))}
          fmt={(v) => kg(v)}
          valueLabel="kg"
          trend
          from={from}
          to={to}
        />
      </ChartFrame>
    </>
  );
}

// ---------------------------------------------------------------------------- calorias

export function CaloriesPanel({ orgId, athleteId, self }: { orgId: string; athleteId?: string; self: boolean }) {
  const [gran, setGran] = useState<"week" | "month">("week");
  const path = self ? `/orgs/${orgId}/me/calories` : `/orgs/${orgId}/athletes/${athleteId}/calories`;
  const st = useAsync(() => api.get<CaloriesSummary>(path, { granularity: gran }), [path, gran]);
  const d = st.data;
  return (
    <section className="section" aria-labelledby={`cal-${self ? "me" : "eq"}`}>
      <div className="row-between">
        <h2 id={`cal-${self ? "me" : "eq"}`}>{t("Calorias gastas")}</h2>
        <div className="seg-toggle" role="group" aria-label={t("Agrupar por")}>
          <button type="button" aria-pressed={gran === "week"} onClick={() => setGran("week")}>{t("Semana")}</button>
          <button type="button" aria-pressed={gran === "month"} onClick={() => setGran("month")}>{t("Mês")}</button>
        </div>
      </div>
      {st.error && <ErrorState error={st.error} onRetry={st.reload} />}
      {!d && st.loading && <Loading />}
      {d && (
        <div style={{ marginTop: 10 }}>
          <dl className="kpis" style={{ marginBottom: 12 }}>
            <Kpi label={gran === "week" ? t("Últimas 13 semanas") : t("Últimos 12 meses")} value={kcal(d.totals.total_kcal)} sub={tn("{n} atividade", "{n} atividades", d.totals.activities)} />
            <Kpi label={t("Do relógio")} value={kcal(d.totals.device_kcal)} />
            {d.estimate_enabled && <Kpi label={t("Estimadas pelo peso")} value={kcal(d.totals.estimated_kcal)} sub={tn("{n} atividade", "{n} atividades", d.totals.estimated_count)} />}
          </dl>
          <ChartFrame
            title={gran === "week" ? t("Calorias por semana") : t("Calorias por mês")}
            subtitle={d.estimate_enabled ? t("Relógio quando o arquivo traz as calorias; senão, estimativa pelo peso e pela distância.") : t("Somente as calorias informadas pelo relógio.")}
            legend={[{ label: t("Do relógio"), color: "var(--chart-1)" }, ...(d.estimate_enabled ? [{ label: t("Estimadas"), color: "var(--chart-ref)" }] : [])]}
            table={{ head: [gran === "week" ? t("Semana de") : t("Mês"), t("Relógio"), t("Estimadas"), t("Atividades")], rows: d.buckets.map((b) => [gran === "week" ? formatDate(b.start) : b.start.slice(0, 7), Math.round(b.device_kcal), Math.round(b.estimated_kcal), b.activities]) }}
            empty={d.totals.activities === 0 ? t("Sem atividades no período.") : null}
          >
            <StackedBars
              data={d.buckets.map((b) => ({
                key: b.start,
                label: gran === "week" ? shortDate(b.start) : b.start.slice(5, 7) + "/" + b.start.slice(2, 4),
                title: gran === "week" ? t("Semana de {start}", { start: formatDate(b.start) }) : b.start.slice(0, 7),
                parts: [
                  { key: "d", label: t("Relógio"), value: b.device_kcal, color: "var(--chart-1)" },
                  ...(d.estimate_enabled ? [{ key: "e", label: t("Estimadas"), value: b.estimated_kcal, color: "var(--chart-ref)" }] : []),
                ],
              }))}
              fmt={kcal}
              labelEvery={gran === "week" ? 2 : 1}
            />
          </ChartFrame>
          <p className="muted" style={{ fontSize: "var(--step--1)", marginTop: 6 }}>
            {d.note}
            {d.totals.missing_count > 0 && " " + (d.estimate_enabled
              ? tn("{n} atividade sem calorias e sem peso/distância para estimar.", "{n} atividades sem calorias e sem peso/distância para estimar.", d.totals.missing_count)
              : tn("{n} atividade sem calorias.", "{n} atividades sem calorias.", d.totals.missing_count))}
          </p>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------- área do atleta

export function BodyPanel({ orgId }: { orgId: string }) {
  const toast = useToast();
  const st = useAsync(() => api.get<MyBody>(`/orgs/${orgId}/me/body`), [orgId]);
  const [weight, setWeight] = useState("");
  const [height, setHeight] = useState("");
  const [day, setDay] = useState("");
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const d = st.data;
  const fe = error?.fieldErrors() ?? {};

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const w = toNum(weight);
    const h = height ? toNum(height) : undefined;
    try {
      await api.put(`/orgs/${orgId}/me/body/weight`, { weight_kg: w, measured_on: day || undefined, height_cm: h });
      toast(t("Peso registrado."));
      setWeight("");
      setHeight("");
      setDay("");
      st.reload();
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  }
  async function patch(path: string, body: unknown, ok: string) {
    try {
      await api.put(`/orgs/${orgId}/me/body/${path}`, body);
      toast(ok);
      st.reload();
    } catch (err) {
      toast((err as ApiError).message);
    }
  }
  async function remove(date: string) {
    if (!window.confirm(t("Excluir o peso de {date}?", { date: formatDate(date) }))) return;
    try {
      await api.delete(`/orgs/${orgId}/me/body/weight/${date}`);
      toast(t("Registro excluído."));
      st.reload();
    } catch (err) {
      toast((err as ApiError).message);
    }
  }

  if (st.error) return <ErrorState error={st.error} onRetry={st.reload} />;
  if (!d) return <Loading />;
  const from = d.measurements[0]?.measured_on ?? d.today;
  const reminderText = d.reminder.reason === "none" ? t("Registre o seu primeiro peso.") : d.reminder.frequency === "weekly" ? t("Você ainda não registrou o peso desta semana.") : t("Você ainda não registrou o peso deste mês.");
  return (
    <>
      {d.reminder.due && (
        <div className="reminder-banner" role="status">
          <span>{reminderText}</span>
        </div>
      )}

      <section className="panel section" aria-labelledby="registrar-peso">
        <div className="panel-head"><h2 id="registrar-peso">{t("Registrar peso")}</h2></div>
        <form className="panel-body stack" onSubmit={save} noValidate>
          <FormError error={error && !Object.keys(fe).length ? error : null} />
          <div className="body-form">
            <TextField label={t("Peso (kg)")} inputMode="decimal" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder={(72.5).toLocaleString(LOCALE)} error={fe.weight_kg} required />
            <TextField label={t("Data")} type="date" value={day} max={d.today} onChange={(e) => setDay(e.target.value)} error={fe.measured_on} />
            <TextField label={d.height_cm ? t("Altura (cm)") : t("Altura (cm) — para o IMC")} inputMode="decimal" value={height} onChange={(e) => setHeight(e.target.value)} placeholder={d.height_cm ? d.height_cm.toLocaleString(LOCALE) : "175"} error={fe.height_cm} />
            <div className="field body-form-submit">
              <span className="label" aria-hidden="true">&nbsp;</span>
              <Button type="submit" busy={busy} disabled={!weight.trim()}>{t("Salvar")}</Button>
            </div>
          </div>
          <p className="muted" style={{ fontSize: "var(--step--1)" }}>{t("Data em branco = hoje. ")}{t("Use sempre as mesmas condições (por exemplo, ao acordar, antes de comer). Só você vê estes dados, a menos que autorize abaixo.")}</p>
        </form>
      </section>

      <section className="panel section" aria-labelledby="imc">
        <div className="panel-head"><h2 id="imc">{t("Peso e IMC")}</h2></div>
        <div className="panel-body stack">
          {d.latest ? <BmiCard bmi={d.latest.bmi} weight={d.latest.weight_kg} date={d.latest.measured_on} /> : <EmptyState title={t("Nenhum peso registrado ainda")}>{t("Registre o primeiro peso acima.")}</EmptyState>}
          {d.needs_height && <p className="muted">{t("Informe a altura em “Registrar peso” para calcular o IMC.")}</p>}
          <p className="muted" style={{ fontSize: "var(--step--1)" }}>{t("IMC = peso ÷ altura² (classificação da OMS para adultos). {bmi_note}", { bmi_note: d.bmi_note })}</p>
        </div>
      </section>

      <section className="section" aria-labelledby="evolucao-peso">
        <h2 id="evolucao-peso" style={{ marginBottom: 10 }}>{t("Evolução do peso")}</h2>
        <WeightCharts body={d} from={from} to={d.today} />
        {d.measurements.length > 0 && (
          <details style={{ marginTop: 10 }}>
            <summary>{t("Meus registros ({v})", { v: d.measurements.length })}</summary>
            <ul className="weight-list">
              {[...d.measurements].reverse().map((m) => (
                <li key={m.measured_on}>
                  <span>{formatDate(m.measured_on)} · {kg(m.weight_kg)}{m.bmi != null ? ` · IMC ${num1(m.bmi)}` : ""}</span>
                  <button type="button" onClick={() => remove(m.measured_on)} aria-label={t("Excluir o peso de {measured_on}", { measured_on: formatDate(m.measured_on) })}>{t("Excluir")}</button>
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      <section className="panel section" aria-labelledby="privacidade-peso">
        <div className="panel-head"><h2 id="privacidade-peso">{t("Lembrete e privacidade")}</h2></div>
        <div className="panel-body stack">
          <SelectField label={t("Com que frequência quero registrar o peso")} value={d.reminder.frequency} onChange={(e) => patch("profile", { reminder_frequency: e.target.value }, t("Frequência atualizada."))}>
            <option value="weekly">{t("Toda semana")}</option>
            <option value="monthly">{t("Todo mês")}</option>
            <option value="off">{t("Sem lembrete")}</option>
          </SelectField>
          <div className={`share-box${d.sharing.granted ? " on" : ""}`}>
            <label className="checkbox">
              <input type="checkbox" checked={d.sharing.granted} onChange={(e) => patch("sharing", { granted: e.target.checked }, e.target.checked ? t("Equipe autorizada a ver peso e IMC.") : t("Compartilhamento desligado."))} />
              <strong>{t("Permitir que o meu treinador e a gestão vejam meu peso e IMC")}</strong>
            </label>
            <p className="muted" style={{ fontSize: "var(--step--1)" }}>{d.sharing.text}</p>
            <p style={{ fontSize: "var(--step--1)" }}>{d.sharing.granted ? <Badge tone="ok">{t("Compartilhando com a equipe")}</Badge> : <Badge tone="off">{t("Só você vê")}</Badge>}</p>
          </div>
        </div>
      </section>

      <CaloriesPanel orgId={orgId} self />
    </>
  );
}

/** Aviso na tela inicial do atleta quando o registro de peso está vencido. */
export function WeightReminder({ orgId }: { orgId: string }) {
  const st = useAsync(() => api.get<MyBody>(`/orgs/${orgId}/me/body`, { from: new Date().toISOString().slice(0, 10), to: new Date().toISOString().slice(0, 10) }), [orgId]);
  if (!st.data?.reminder.due) return null;
  return (
    <div className="reminder-banner" role="status" style={{ marginTop: 12 }}>
      <span>{st.data.reminder.reason === "none" ? t("Registre o seu peso para acompanhar IMC e evolução.") : st.data.reminder.frequency === "weekly" ? t("Hora de registrar o peso desta semana.") : t("Hora de registrar o peso deste mês.")}</span>
      <Link to={`/org/${orgId}/atleta/corpo`} className="btn btn-secondary btn-sm">{t("Registrar peso")}</Link>
    </div>
  );
}

// ---------------------------------------------------------------------------- área da equipe

export function BodyStaff({ orgId, athleteId }: { orgId: string; athleteId: string }) {
  const st = useAsync(() => api.get<StaffBody>(`/orgs/${orgId}/athletes/${athleteId}/body`), [orgId, athleteId]);
  const d = st.data;
  return (
    <>
      <section className="section" aria-labelledby="peso-equipe">
        <h2 id="peso-equipe" style={{ marginBottom: 10 }}>{t("Peso e IMC")}</h2>
        {st.loading && !d && <Loading />}
        {st.error && <ErrorState error={st.error} onRetry={st.reload} />}
        {d && !d.shared && (
          <div className="panel">
            <EmptyState title={t("O atleta não compartilha peso e IMC")}>{t("Esses dados são de saúde e só aparecem aqui se o atleta autorizar, no app dele. As calorias do relógio continuam disponíveis abaixo.")}</EmptyState>
          </div>
        )}
        {d && d.shared && (
          <div className="stack">
            <div className="panel"><div className="panel-body">
              {d.latest ? <BmiCard bmi={d.latest.bmi} weight={d.latest.weight_kg} date={d.latest.measured_on} /> : <EmptyState title={t("Nenhum peso registrado")} />}
            </div></div>
            <WeightCharts body={d} from={d.measurements[0]?.measured_on ?? new Date().toISOString().slice(0, 10)} to={new Date().toISOString().slice(0, 10)} />
            <p className="muted" style={{ fontSize: "var(--step--1)" }}>{t("Dados sensíveis compartilhados com autorização do atleta; cada consulta fica registrada na auditoria. {bmi_note}", { bmi_note: d.bmi_note })}</p>
          </div>
        )}
      </section>
      <CaloriesPanel orgId={orgId} athleteId={athleteId} self={false} />
    </>
  );
}

